import { useEffect, useMemo } from 'react';
import { createRoot } from 'react-dom/client';
import type { Event } from '../../interfaces/Calendar.model';
import CalendarCell from './CalendarCell';
import CalendarHeadingCell from './CalendarHeadingCell';
import '../../i18n';
import i18n from 'i18next';
import { ErrorBoundary } from 'react-error-boundary';
import { useCalendarEvents } from '../../hooks/useCalendarEvents';
import { useCalendarNavigation } from '../../hooks/useCalendarNavigation';
import { useCalendarUrl } from '../../hooks/useCalendarUrl';
import { getLocalizedDayNames, getMonthTimeframe, getWeeksInMonth } from '../../utils/dateUtils';
import { CalendarHeader } from './CalendarHeader';
import { CalendarLegend } from './CalendarLegend';

interface CalendarProps {
    locale: string;
    initYear: number;
    initMonth: number;
    events: Event[];
}

const Calendar = (props: CalendarProps) => {
    const { locale, initYear, initMonth, events } = props;

    const { month, year, prevMonth, nextMonth } = useCalendarNavigation(initMonth, initYear);

    useEffect(() => {
        i18n.changeLanguage(locale);
    }, [locale]);

    const { startOfTimeFrame, endOfTimeFrame } = useMemo(
        () => getMonthTimeframe(month - 1, year),
        [month, year]
    );
    const thisMonthEvents = useCalendarEvents(events, startOfTimeFrame, endOfTimeFrame);

    useCalendarUrl(year, month);

    // Build a per-day event map once per month change so each cell can do
    // an O(1) lookup instead of filtering the full event list per cell.
    // Walk via setDate so DST shifts (23h spring, 25h autumn) don't drop
    // or duplicate a day.
    const eventsByDay = useMemo(() => {
        const map = new Map<string, Event[]>();
        for (const event of thisMonthEvents) {
            const cursor = new Date(event.startDate);
            cursor.setHours(0, 0, 0, 0);
            const last = new Date(event.endDate);
            last.setHours(0, 0, 0, 0);
            while (cursor <= last) {
                const key = cursor.toDateString();
                const list = map.get(key);
                if (list) list.push(event);
                else map.set(key, [event]);
                cursor.setDate(cursor.getDate() + 1);
            }
        }
        return map;
    }, [thisMonthEvents]);

    const weeks = useMemo(() => getWeeksInMonth(month, year), [month, year]);
    const localizedDays = useMemo(() => getLocalizedDayNames(locale), [locale]);

    return (
        <ErrorBoundary fallback={<div>Something went wrong</div>}>
            <section className="bg-beige after:bg-backgroundGreen pt-0 px-4 after:h-80 after:absolute after:bottom-0 after:left-0 after:w-full relative overflow-hidden after:z-0">
                <div className="container z-10 relative">
                    <CalendarHeader
                        year={year}
                        month={month}
                        locale={locale}
                        onPrev={prevMonth}
                        onNext={nextMonth}
                    />
                </div>
                <div className="container z-10 relative">
                    <div
                        className="flex flex-wrap pb-12 pb-0 justify-between items-center"
                        id="kalender"
                    >
                        <div className="w-full relative pt-12 overflow-scroll md:overflow-auto">
                            <table className="table table-bordered w-full min-w-1000 overflow-scroll md:overflow-visible">
                                <thead>
                                    <tr id="days_of_week" className="active bg-backgroundGreen">
                                        {localizedDays.map((day, index) => (
                                            <CalendarHeadingCell key={index} dayOfWeek={day} />
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {weeks.map((week, weekIndex) => (
                                        <tr key={`week-${weekIndex}`}>
                                            {week.map((day, dayIndex) => {
                                                const isCurrentMonth = day.getMonth() === month - 1;
                                                return (
                                                    <CalendarCell
                                                        key={`day-${weekIndex}-${dayIndex}`}
                                                        date={day}
                                                        isCurrentMonth={isCurrentMonth}
                                                        events={eventsByDay.get(day.toDateString()) ?? []}
                                                    />
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <CalendarLegend events={events} />
                </div>
            </section>
        </ErrorBoundary>
    );
};

const calendarElements = document.querySelectorAll(
    '.calendar-component'
) as NodeListOf<HTMLElement>;
calendarElements.forEach((element) => {
    const root = createRoot(element);

    try {
        const locale = element.dataset.locale ?? 'nl';
        const year = parseInt(element.dataset.initYear ?? new Date().getFullYear().toString(), 10);
        const month = parseInt(element.dataset.initMonth ?? (new Date().getMonth() + 1).toString(), 10);
        const events = JSON.parse(element.dataset.events ?? '[]') as Event[];

        if (month < 1 || month > 12) throw new Error(`Invalid month: ${month}`);
        if (year < 1900 || year > 2100) throw new Error(`Invalid year: ${year}`);
        if (!Array.isArray(events)) throw new Error('Events must be an array');

        root.render(
            <Calendar locale={locale} initYear={year} initMonth={month} events={events} />
        );
    } catch (error) {
        console.error('Failed to initialize calendar:', error);
    }
});
